import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo, KenyaStripe } from "@/components/Logo";
import { ArrowLeft, Globe, Instagram, Twitter, Trophy, Star, Crown, LogIn, ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PublicTeam() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/teams/${slug}`);
        setTeam(data);
      } catch (e) {
        setErr(formatApiError(e));
      }
      setLoading(false);
    })();
  }, [slug]);

  const canEdit = user && (user.role === "super_admin" || user.role === "league_admin" || (user.role === "owner" && user.team_id === team?.id));

  if (loading) return <div className="min-h-screen bg-[#060d09] flex items-center justify-center text-zinc-400">Loading…</div>;

  if (err || !team) {
    return (
      <div className="min-h-screen bg-[#060d09] flex items-center justify-center p-6">
        <Card className="p-10 max-w-md text-center bg-[#11241b] border-white/10" data-testid="team-not-found">
          <Trophy className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
          <div className="font-anton text-3xl uppercase text-white">Team Not Found</div>
          <p className="text-zinc-400 mt-3 text-sm">No franchise is registered at <span className="text-white font-mono">/team/{slug}</span> yet.</p>
          <Button asChild className="mt-5 bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest" data-testid="team-not-found-home">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Home</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const teamColor = team.color || "#00A86B";
  const social = team.social || {};

  return (
    <div className="min-h-screen bg-[#060d09] text-white" data-testid="public-team-page">
      <div className="kenya-stripe" />
      <header className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="team-nav-home"><Logo variant="inline" className="h-10" /></Link>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button asChild size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="team-edit-btn">
              <Link to={`/team/${team.slug}/edit`}>Edit Team Page</Link>
            </Button>
          )}
          {!user && (
            <Button asChild size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Link to="/admin"><LogIn className="w-3.5 h-3.5 mr-1.5" /> Admin</Link>
            </Button>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-white/5">
        <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 20% 20%, ${teamColor}22, transparent 60%), radial-gradient(circle at 80% 80%, #E3242422, transparent 60%)` }} />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-[1fr_2fr] gap-10 items-center">
          <div className="flex justify-center lg:justify-start">
            {team.logo_url ? (
              <div className="w-48 h-48 rounded-2xl bg-white p-4 shadow-2xl" data-testid="team-logo">
                <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-48 h-48 rounded-2xl flex items-center justify-center font-anton text-7xl text-white" style={{ background: teamColor }} data-testid="team-logo-placeholder">
                {team.name?.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: teamColor }}>Mara Padel League · Franchise</div>
            <h1 className="font-anton text-6xl md:text-7xl uppercase tracking-tighter text-white mt-3 leading-[0.9]" data-testid="team-name">{team.name}</h1>
            <KenyaStripe className="mt-4 w-32" />
            {team.owner_name && (
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-300" data-testid="team-owner">
                <Crown className="w-4 h-4" style={{ color: teamColor }} /> Owned by <span className="text-white font-bold">{team.owner_name}</span>
              </div>
            )}
            {team.description && <p className="mt-5 text-zinc-300 max-w-2xl text-[15px] leading-relaxed" data-testid="team-description">{team.description}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              {social.website && <SocialBtn href={social.website} icon={Globe} label="Website" testid="team-social-website" />}
              {social.instagram && <SocialBtn href={social.instagram} icon={Instagram} label="Instagram" testid="team-social-instagram" />}
              {social.twitter && <SocialBtn href={social.twitter} icon={Twitter} label="Twitter" testid="team-social-twitter" />}
            </div>
          </div>
        </div>
      </section>

      {/* ROSTER */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="team-roster-section">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: teamColor }}>The Squad</div>
            <h2 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-2">Roster · {team.players.length}</h2>
          </div>
        </div>
        {team.players.length === 0 ? (
          <Card className="p-10 text-center bg-[#11241b] border-white/10 border-dashed" data-testid="team-roster-empty">
            <Trophy className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
            <div className="font-anton text-2xl uppercase text-white">Squad Drops At The Auction</div>
            <p className="text-sm text-zinc-400 mt-2">Roster will be filled live on auction day.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {team.players.map((p) => (
              <Card key={p.id} className="relative overflow-hidden bg-[#11241b] border-white/10 group" data-testid={`team-player-${p.id}`}>
                <div className="aspect-[3/4] relative">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#16322a] to-[#06210f]">
                      <span className="font-anton text-6xl text-white/40">{p.name?.slice(0,1) || "?"}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-3 left-3 flex flex-col gap-1">
                    {p.tier && <span className="text-[10px] uppercase tracking-widest text-black px-2 py-0.5 rounded font-bold" style={{ background: teamColor }}>{p.tier}</span>}
                    {p.is_international && <span className="text-[10px] uppercase tracking-widest bg-[#E32424] text-white px-2 py-0.5 rounded font-bold flex items-center gap-1"><Star className="w-3 h-3" /> INTL</span>}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-anton text-xl uppercase tracking-tight text-white truncate">{p.name}</div>
                    <div className="text-xs text-zinc-300"><span className="capitalize">{p.category}</span> · <span className="font-bold" style={{ color: teamColor }}>{p.rating}</span></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <div className="kenya-stripe" />
      <footer className="max-w-6xl mx-auto px-6 py-8 text-center text-xs text-zinc-500">
        <Link to="/" className="hover:text-white" data-testid="team-back-home">← Back to Mara Padel League</Link>
      </footer>
    </div>
  );
}

function SocialBtn({ href, icon: Icon, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-zinc-200 hover:bg-white/10 hover:text-white transition-colors"
    >
      <Icon className="w-4 h-4" /> {label} <ExternalLink className="w-3 h-3 opacity-50" />
    </a>
  );
}
